import { Loader2, FileText, Plus } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  REQUIREMENT_TAB,
  useRequirementLabels,
  type PublishRequirement,
} from "@/lib/recipes/publish-requirements";

interface PublishRecipeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isPending?: boolean;
  /**
   * What the recipe still needs, from the server's own rule set. Non-empty
   * means the API would refuse this publish, so the dialog refuses it here
   * instead of sending a request it knows comes back 422 (T-705).
   */
  missing?: PublishRequirement[];
  /** Take the cook to the tab where a requirement is dealt with. */
  onFix?: (tab: string) => void;
}

export function PublishRecipeDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending = false,
  missing = [],
  onFix,
}: PublishRecipeDialogProps) {
  const { t } = useLingui();
  const labels = useRequirementLabels();
  const firstMissing = missing[0];
  const blocked = firstMissing !== undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="max-w-[500px] p-0 gap-0 overflow-hidden rounded-xl border border-(--shell-border)"
      >
        {/* Illustration block */}
        <div className="relative flex h-[248px] items-center justify-center bg-(--color-surface-muted) border-b border-(--color-border) overflow-hidden">
          <div className="relative">
            <FileText
              className="absolute -top-1.5 left-1.5 size-40 text-(--gray-300)"
              strokeWidth={1}
            />
            <FileText
              className="relative size-40 text-(--color-text-primary)"
              strokeWidth={1}
            />
          </div>
          <Plus
            className="absolute top-12 right-[148px] size-10 text-(--gray-300)"
            strokeWidth={1.5}
          />
          <Plus
            className="absolute bottom-14 left-[148px] size-10 text-(--gray-300)"
            strokeWidth={1.5}
          />
          <Plus
            className="absolute top-3 left-[196px] size-7 text-(--gray-300)"
            strokeWidth={1.5}
          />
          <span className="absolute top-[78px] left-[212px] size-1.5 rounded-full bg-(--color-surface-accent)" />
          <span className="absolute bottom-[44px] right-[140px] size-1.5 rounded-full bg-(--color-surface-accent)" />
          <span className="absolute top-[40px] left-[197px] size-1.5 rounded-full bg-(--color-surface-accent)" />
        </div>

        {/* Text block */}
        <div className="flex flex-col items-center gap-3 p-5 text-center">
          <DialogTitle className="text-2xl font-medium text-(--color-text-primary)">
            {blocked ? t`Not ready to publish` : t`Publish Recipe?`}
          </DialogTitle>
          {blocked ? (
            <>
              <p className="text-base text-(--color-text-secondary)">
                {t`This recipe still needs a few things before anyone can cook from it:`}
              </p>
              <ul className="flex list-disc flex-col gap-1 self-start pl-5 text-left text-base text-(--color-text-primary)">
                {missing.map((requirement) => (
                  <li key={requirement}>{labels[requirement]}</li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-base text-(--color-text-secondary)">
              {t`You're about to publish this recipe. It will become visible to all regions and locations it is shared with. You'll still be able to edit and make changes after publishing.`}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-5">
          <Button
            variant="outline"
            className="flex-1 h-[52px] rounded-[10px] border-(--shell-border) text-base font-medium text-(--color-text-primary)"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {blocked ? t`Close` : t`Cancel`}
          </Button>
          {blocked ? (
            <Button
              className="flex-1 h-[52px] rounded-[10px] bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg) text-base font-medium"
              onClick={() => onFix?.(REQUIREMENT_TAB[firstMissing])}
            >
              {t`Fix what's missing`}
            </Button>
          ) : (
            <Button
              className="flex-1 h-[52px] rounded-[10px] bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg) text-base font-medium"
              onClick={onConfirm}
              disabled={isPending}
            >
              {isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t`Publish recipe`}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
